import { guidFor } from '@ember/object/internals';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import {
  clampHeaderImageCropY,
  headerImageCropYAfterDrag,
} from 'admin-panel/utils/page-header-image-crop';

type PointerDrag = {
  pointerId: number;
  startClientY: number;
  startCropY: number;
};

interface PanelCategoryPageHeaderImageCropSurfaceSignature {
  Args: {
    cropY: number;
    imageWidth: number;
    imageHeight: number;
    onChange: (cropY: number) => void;
    onCancel: () => void;
    onSave: () => void;
  };
}

export default class PanelCategoryPageHeaderImageCropSurfaceComponent extends Component<PanelCategoryPageHeaderImageCropSurfaceSignature> {
  private surfaceElement: HTMLElement | null = null;
  private pointerDrag: PointerDrag | null = null;
  readonly instructionId = `${guidFor(this)}-instruction`;
  readonly keyboardGuidanceId = `${guidFor(this)}-keyboard-guidance`;

  willDestroy(): void {
    this.releasePointerCapture();
    super.willDestroy();
  }

  @action
  registerSurface(element: Element) {
    if (element instanceof HTMLElement) {
      this.surfaceElement = element;
      element.focus();
    }
  }

  @action
  clearSurface(element: Element) {
    if (this.surfaceElement === element) {
      this.releasePointerCapture();
      this.surfaceElement = null;
    }
  }

  @action
  handleKeydown(event: KeyboardEvent) {
    const step = event.shiftKey ? 10 : 1;
    let cropY: number | null = null;

    switch (event.key) {
      case 'ArrowUp':
        cropY = this.args.cropY + step;
        break;
      case 'ArrowDown':
        cropY = this.args.cropY - step;
        break;
      case 'Home':
        cropY = 0;
        break;
      case 'End':
        cropY = 100;
        break;
      case 'Enter':
        event.preventDefault();
        this.args.onSave();
        return;
      case 'Escape':
        event.preventDefault();
        this.args.onCancel();
        return;
      default:
        return;
    }

    event.preventDefault();
    this.args.onChange(clampHeaderImageCropY(cropY));
  }

  @action
  startPointerDrag(event: PointerEvent) {
    if (
      !event.isPrimary ||
      (event.pointerType === 'mouse' && event.button !== 0)
    ) {
      return;
    }

    const surface = event.currentTarget;
    if (!(surface instanceof HTMLElement)) return;

    event.preventDefault();
    this.pointerDrag = {
      pointerId: event.pointerId,
      startClientY: event.clientY,
      startCropY: this.args.cropY,
    };
    surface.setPointerCapture(event.pointerId);
  }

  @action
  updatePointerDrag(event: PointerEvent) {
    const drag = this.pointerDrag;
    const surface = this.surfaceElement;
    if (!drag || event.pointerId !== drag.pointerId || !surface) return;

    const { width, height } = surface.getBoundingClientRect();
    if (!width || !height) return;

    this.args.onChange(
      headerImageCropYAfterDrag({
        startCropY: drag.startCropY,
        deltaY: event.clientY - drag.startClientY,
        viewportWidth: width,
        viewportHeight: height,
        imageWidth: this.args.imageWidth,
        imageHeight: this.args.imageHeight,
      })
    );
  }

  @action
  finishPointerDrag(event: PointerEvent) {
    if (event.pointerId === this.pointerDrag?.pointerId) {
      this.pointerDrag = null;
    }
  }

  private releasePointerCapture() {
    const drag = this.pointerDrag;
    const surface = this.surfaceElement;

    this.pointerDrag = null;
    if (drag && surface?.hasPointerCapture(drag.pointerId)) {
      surface.releasePointerCapture(drag.pointerId);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Panel::Category::PageHeaderImageCropSurface': typeof PanelCategoryPageHeaderImageCropSurfaceComponent;
    'panel/category/page-header-image-crop-surface': typeof PanelCategoryPageHeaderImageCropSurfaceComponent;
  }
}
